import {FormattedMessage} from 'react-intl';

import {useHistory} from 'react-router-dom';

import {ModalDialog, ModalSize} from '@time-webkit/all/organisms/modal-dialog';
import {Alert, AlertType} from '@time-webkit/all/atoms/alert';
import {LockIcon} from '@time-webkit/all/icons/lock';

import {TypographySize, useTypography} from '@time-webkit/all/hooks/typography';

import {type Channel, type ChannelType} from 'mattermost-redux/types/channels';
import {localizeMessage} from 'utils/utils';

import {type Team} from '@mattermost/types/teams';

import TildeIcon from 'components/widgets/icons/tilde_icon';

import Constants from 'utils/constants';

import styles from './styles.module.css';

export type Props = {
    channel: {
        id: Channel['id'];
        display_name: string;
        type: ChannelType;
    };
    prevChannel?: Channel | null;
    prevTeam?: Team | null;
    onExited: () => void;
    actions: {
        leaveChannel: (channelId: string) => Promise<{data: boolean}>;
        setCurrentChannel: (channelId: string) => void;
    };
}

export const LeaveLastChannelModal = (props: Props) => {
    const history = useHistory();
    const handleSubmit = () => {
        const {actions, channel} = props;

        if (channel) {
            const channelId = channel.id;

            actions.leaveChannel(channelId).then((result: {data: boolean}) => {
                if (result.data) {
                    props.onExited();

                    if (props.prevTeam && props.prevChannel) {
                        history.push(`/${props.prevTeam.name}/channels/${props.prevChannel.name}`);
                        return;
                    }
                    if (props.prevTeam) {
                        history.push(`/${props.prevTeam.name}`);
                    }
                }
            });
        }
    };
    const title = (
        <div className={styles.title}>
            <FormattedMessage
                id='leave_last_channel_modal.title'
                defaultMessage='Leave {channel}?'
                ignoreTag={true}
                values={{
                    channel: <>
                        {props.channel.type === Constants.PRIVATE_CHANNEL ? (
                            <LockIcon
                                className={styles.icon}
                                width={16}
                                height={16}
                            />
                        ) : (
                            <TildeIcon
                                className={styles.icon}
                                size={16}
                            />
                        )}
                        {props.channel.display_name}
                    </>,
                }}
            />
        </div>
    );
    const description = localizeMessage(
        'leave_last_channel_modal.description',
        'If you leave the channel you will lose your saved messages, your reminders and scheduled messages will be deleted.',
    );
    const alert = localizeMessage(
        'leave_last_channel_modal.alert',
        'When you leave a channel, you will leave the team. You can return back only by invitation from the participants.',
    );
    const confirmText = localizeMessage('leave_last_channel_modal.leave', 'Leave channel');
    const cancelText = localizeMessage('leave_last_channel_modal.stay', 'Stay');
    const typoCls = useTypography({
        size: TypographySize.BodyL,
    });

    return (
        <ModalDialog
            title={title}
            isOpen={true}
            confirmText={confirmText}
            cancelText={cancelText}
            size={ModalSize.MEDIUM}
            autoFocusCancelBtn={false}
            onClose={props.onExited}
            onConfirm={handleSubmit}
            isConfirmDestructive={true}
            nodeRef={{} as React.MutableRefObject<HTMLElement>}
            data-qa='leave-channel-modal'
        >
            <p className={typoCls}>{description}</p>
            <Alert message={alert} type={AlertType.Warning} />
        </ModalDialog>
    );
};
